import { Bound } from '@blocksuite/global/gfx';

import type { ResizeHandle } from './manager.js';

/**
 * Handles a multi-selection (or group) shows: every handle, unless one member
 * cannot be resized at all (e.g. an unbound connector or a mind map), which
 * keeps the whole selection fixed. Member-specific handle limits apply to the
 * member's own share of the resize, not to the selection.
 */
export function resolveSelectionResizeHandles(
  memberHandles: readonly (readonly ResizeHandle[])[],
  defaultHandles: readonly ResizeHandle[]
): ResizeHandle[] {
  if (memberHandles.length === 1) {
    const [only] = memberHandles;
    return defaultHandles.filter(handle => only.includes(handle));
  }
  if (memberHandles.some(handles => handles.length === 0)) return [];
  return [...defaultHandles];
}

export function isHandleRatioLocked(
  lockRatio: boolean | readonly ResizeHandle[],
  handle: ResizeHandle
): boolean {
  return (
    lockRatio === true ||
    (Array.isArray(lockRatio) && lockRatio.includes(handle))
  );
}

/**
 * A member that only resizes proportionally follows a one-axis stretch of its
 * selection with a uniform scale along the dragged axis, centred where the
 * stretch moved it.
 */
export function proportionalMemberBound(
  originalBound: Bound,
  stretchedBound: Bound,
  handle: ResizeHandle
): Bound {
  const horizontal = handle === 'left' || handle === 'right';
  const vertical = handle === 'top' || handle === 'bottom';
  if (!horizontal && !vertical) return stretchedBound;

  const scale = horizontal
    ? stretchedBound.w / originalBound.w
    : stretchedBound.h / originalBound.h;
  const w = originalBound.w * scale;
  const h = originalBound.h * scale;
  const [cx, cy] = stretchedBound.center;
  return new Bound(cx - w / 2, cy - h / 2, w, h);
}
